import {
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import { Box, Stack } from '@mui/system';
import { useCallback, useEffect, useState } from 'react';
import { useNotify, useTranslate } from 'react-admin';
import { SplashScreen } from 'src/components/loading-screen';
import { EmptyContent } from 'src/components/empty-content';
import { listPoolMembers, ProxyNode } from '../../../services/proxyManagerService';
import { QUARANTINED_LEVELS } from '../constants';
import { HealthCheckButton } from '../HealthCheckButton';
import { ResetQuarantineButton } from '../ResetQuarantineButton';

interface MembersDialogProps {
  poolId?: string;
  poolName?: string;
  /** Targets configured on the pool — used as the health check targets of each member. */
  healthCheckTargets?: string[];
  open: boolean;
  onClose: () => void;
}

const PAGE_SIZE = 50;

const MembersDialog = ({
  poolId,
  poolName,
  healthCheckTargets = [],
  open,
  onClose,
}: MembersDialogProps) => {
  const translate = useTranslate();
  const notify = useNotify();
  const [members, setMembers] = useState<ProxyNode[]>([]);
  const [nextCursor, setNextCursor] = useState<string | null>(null);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);

  const fetchMembers = useCallback(
    async (cursor?: string) => {
      if (!poolId) return;
      setLoading(true);
      try {
        const result = await listPoolMembers(poolId, { first: PAGE_SIZE, after: cursor });
        setMembers((prev) => (cursor ? [...prev, ...result.nodes] : result.nodes));
        setNextCursor(result.nextCursor);
        setTotal(result.totalCount);
      } catch (error: any) {
        notify(error?.message || translate('module.proxyManager.unknownError'), { type: 'error' });
      } finally {
        setLoading(false);
      }
    },
    [poolId, notify, translate]
  );

  // Reset / health check change the row, so reload the list from the first page.
  const handleActionSuccess = useCallback(() => {
    setNextCursor(null);
    fetchMembers();
  }, [fetchMembers]);

  useEffect(() => {
    if (open && poolId) {
      setMembers([]);
      setNextCursor(null);
      setTotal(0);
      fetchMembers();
    }
  }, [open, poolId, fetchMembers]);

  return (
    <Dialog open={open} onClose={onClose} maxWidth="lg" fullWidth>
      <DialogTitle>
        {translate('module.proxyManager.membersTitle', { _: 'Pool Members' })}
        {poolName ? ` — ${poolName}` : ''}
        {total ? ` (${total})` : ''}
      </DialogTitle>
      <DialogContent dividers sx={{ position: 'relative', minHeight: 200 }}>
        {loading && members.length === 0 ? (
          <Box sx={{ py: 6, display: 'flex', justifyContent: 'center' }}>
            <SplashScreen />
          </Box>
        ) : members.length === 0 ? (
          <EmptyContent />
        ) : (
          <Box sx={{ overflow: 'auto' }}>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>{translate('module.proxyManager.protocol')}</TableCell>
                  <TableCell>{translate('module.proxyManager.address')}</TableCell>
                  <TableCell>{translate('module.proxyManager.username')}</TableCell>
                  <TableCell>{translate('module.proxyManager.tier')}</TableCell>
                  <TableCell>{translate('module.proxyManager.geo')}</TableCell>
                  <TableCell>{translate('module.proxyManager.ispName')}</TableCell>
                  <TableCell>{translate('module.proxyManager.source')}</TableCell>
                  <TableCell>{translate('module.proxyManager.quarantineLevel')}</TableCell>
                  <TableCell>{translate('module.proxyManager.tags')}</TableCell>
                  <TableCell align="right">{translate('common.actions')}</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {members.map((member) => {
                  const geo = member.geo;
                  const geoText = geo
                    ? [geo.countryCode, geo.city, geo.region].filter(Boolean).join(' / ') || '-'
                    : '-';
                  return (
                    <TableRow key={member.id}>
                      <TableCell>{member.protocol}</TableCell>
                      <TableCell>{`${member.host}:${member.port}`}</TableCell>
                      <TableCell>{member.username || '-'}</TableCell>
                      <TableCell>{member.tier}</TableCell>
                      <TableCell>{geoText}</TableCell>
                      <TableCell>{member.isp?.name || '-'}</TableCell>
                      <TableCell>{member.source || '-'}</TableCell>
                      <TableCell>
                        {member.quarantineLevel ? (
                          <Typography
                            variant="body2"
                            sx={{
                              color: QUARANTINED_LEVELS.includes(member.quarantineLevel)
                                ? 'error.main'
                                : 'success.main',
                              fontWeight: 500,
                              minWidth: 100,
                              textTransform: 'capitalize',
                            }}
                          >
                            {member.quarantineLevel}
                          </Typography>
                        ) : '-'}
                      </TableCell>
                      <TableCell>
                        <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap>
                          {(member.tags ?? []).length
                            ? member.tags.map((tag) => (
                                <Chip key={tag} label={tag} size="small" variant="outlined" />
                              ))
                            : '-'}
                        </Stack>
                      </TableCell>
                      <TableCell align="right">
                        <Stack direction="column" spacing={1} justifyContent="flex-end">
                          <ResetQuarantineButton
                            proxyId={member.id}
                            address={`${member.host}:${member.port}`}
                            quarantineLevel={member.quarantineLevel}
                            onSuccess={handleActionSuccess}
                          />
                          <HealthCheckButton
                            proxyId={member.id}
                            address={`${member.host}:${member.port}`}
                            defaultTargets={healthCheckTargets}
                            protocol={member.protocol}
                            onSuccess={handleActionSuccess}
                          />
                        </Stack>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
            {nextCursor && (
              <Box sx={{ display: 'flex', justifyContent: 'center', pt: 2 }}>
                <Button variant="outlined" size="small" disabled={loading} onClick={() => fetchMembers(nextCursor)}>
                  {translate('module.proxyManager.loadMore', { _: 'Load more' })}
                </Button>
              </Box>
            )}
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', pt: 1 }}>
              {translate('module.proxyManager.showingCount', {
                _: 'Showing %{shown} of %{total}',
                shown: members.length,
                total,
              })}
            </Typography>
          </Box>
        )}
      </DialogContent>
      <DialogActions>
        <Button variant="outlined" onClick={onClose}>
          {translate('actions.close', { _: 'Close' })}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default MembersDialog;
